import { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Layers, Loader2, FilePlus2, Paperclip, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { requestsApi, documentsApi } from '../lib/api';

const CREATE_NEW_KEY = '__create_new__';

export function GenerateBundleDialog({ open, onOpenChange, request, documents = [], onGenerated, lockedDocType = null, lockedReason = null }) {
  const [checked, setChecked] = useState({});
  const [includeCover, setIncludeCover] = useState(true);
  const [name, setName] = useState('');
  const [docTypeSel, setDocTypeSel] = useState('');
  const [customDocType, setCustomDocType] = useState('');
  // iter110: request-scoped list of EXISTING bundle doc types (no preset types from
  // the request-type-settings — bundles must use a custom name per user spec).
  const [bundleTypes, setBundleTypes] = useState([]); // [{doc_type, latest_version, ...}]
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!open || !request?.id) return;
    documentsApi
      .listBundleDocTypes(request.id)
      .then((res) => setBundleTypes(Array.isArray(res.data) ? res.data : []))
      .catch(() => setBundleTypes([]));
    setName('');
    if (lockedDocType) {
      // Coming from "Upload New Version" inside an existing bundle thread — pre-pick
      // the chain's doc_type and DO NOT let the user change it.
      setDocTypeSel(lockedDocType);
      setCustomDocType('');
    } else {
      setDocTypeSel('');
      setCustomDocType('');
    }
    setChecked({});
  }, [open, request?.id, request?.type, lockedDocType]);

  // Bundleable candidates: real source docs AND thread attachments. Hide existing
  // bundles + signed PDFs + bundle types.
  const selectable = useMemo(() => (
    documents.filter((d) => (
      !d.is_ft_bundle &&
      !((d.doc_type || '').startsWith('Signed_')) &&
      !((d.doc_type || '').endsWith('_Signature_Bundle')) &&
      (d.doc_type || '') !== 'Bundle'
    ))
  ), [documents]);
  const selectedIds = selectable.filter((d) => checked[d.id]).map((d) => d.id);

  const finalDocType = docTypeSel === CREATE_NEW_KEY ? customDocType.trim() : docTypeSel;

  const handleGenerate = async () => {
    if (selectedIds.length === 0) return toast.error('Select at least one document to bundle');
    if (!name.trim()) return toast.error('Please give the bundle a name');
    if (!finalDocType) return toast.error('Please choose or create a document type');
    setGenerating(true);
    try {
      const res = await requestsApi.generateBundle(request.id, {
        document_ids: selectedIds,
        include_cover_pages: includeCover,
        name: name.trim(),
        doc_type: finalDocType,
      });
      toast.success(`Bundle generated: ${res.data.filename}`);
      setChecked({});
      onOpenChange(false);
      onGenerated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to generate the bundle');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!generating) onOpenChange(o); }}>
      <DialogContent
        className="max-w-2xl w-[92vw] max-h-[90vh] overflow-hidden p-0 flex flex-col"
        data-testid="generate-bundle-dialog"
      >
        <DialogHeader className="px-6 pt-5 pb-3 border-b border-slate-200">
          <DialogTitle className="flex items-center gap-2">
            <Layers className="w-5 h-5 text-blue-700" />
            Generate Bundle PDF
          </DialogTitle>
          <DialogDescription>
            Combine selected documents into one PDF. Give it a name and a document type — it will be saved
            to the Documents tab with its own discussion thread, just like an uploaded document.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5 min-w-0">
          {/* Bundle name + doc type */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="min-w-0">
              <Label htmlFor="bundle-name" className="text-xs font-semibold uppercase tracking-wide text-slate-500">Bundle name</Label>
              <Input
                id="bundle-name"
                placeholder="e.g. Final Submission Pack"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1"
                data-testid="bundle-name-input"
              />
              <p className="text-[11px] text-slate-400 mt-1 truncate">
                Saved as <code>{request?.ref_no || request?.id || '<ref>'}_<span className="text-slate-600">{name || 'name'}</span>.pdf</code>
              </p>
            </div>
            <div className="min-w-0">
              <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Document type</Label>
              {lockedDocType ? (
                <>
                  <div
                    className="mt-1 flex items-center gap-2 px-3 py-2 rounded-md border border-indigo-200 bg-indigo-50 text-sm text-indigo-900"
                    data-testid="bundle-doctype-locked"
                  >
                    <Layers className="w-4 h-4 text-indigo-600" />
                    <span className="font-medium truncate">{lockedDocType}</span>
                    <Badge className="ml-auto text-[10px] bg-indigo-100 text-indigo-700 border border-indigo-300">
                      New version
                    </Badge>
                  </div>
                  <p className="text-[11px] text-indigo-700 mt-1">
                    {lockedReason || 'Locked because you are uploading a new version into the existing bundle chain.'}
                  </p>
                </>
              ) : (
                <>
                  <Select value={docTypeSel} onValueChange={setDocTypeSel}>
                    <SelectTrigger className="mt-1" data-testid="bundle-doctype-select">
                      <SelectValue placeholder={bundleTypes.length === 0 ? 'Create a new bundle type…' : 'Select a bundle type…'} />
                    </SelectTrigger>
                    <SelectContent>
                      {bundleTypes.map((t) => (
                        <SelectItem
                          key={t.doc_type}
                          value={t.doc_type}
                          data-testid={`bundle-doctype-opt-${t.doc_type}`}
                        >
                          {t.doc_type} <span className="text-xs text-slate-400">· latest v{t.latest_version}</span>
                        </SelectItem>
                      ))}
                      <SelectItem value={CREATE_NEW_KEY} data-testid="bundle-doctype-create-new">➕ Create new type…</SelectItem>
                    </SelectContent>
                  </Select>
                  {docTypeSel === CREATE_NEW_KEY && (
                    <Input
                      placeholder="New type name (e.g. Quarterly Report)"
                      value={customDocType}
                      onChange={(e) => setCustomDocType(e.target.value)}
                      className="mt-2"
                      data-testid="bundle-doctype-custom-input"
                    />
                  )}
                  {docTypeSel && docTypeSel !== CREATE_NEW_KEY && (
                    <p className="text-[11px] text-indigo-700 mt-1">
                      A new version (v{((bundleTypes.find(b => b.doc_type === docTypeSel) || {}).latest_version || 0) + 1}) will be added to the existing bundle chain.
                    </p>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Source docs */}
          <div className="min-w-0">
            <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Source documents</Label>
            <div className="mt-1 max-h-[36vh] overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100">
              {selectable.length === 0 ? (
                <p className="text-sm text-slate-500 italic p-3">No bundleable documents on this request.</p>
              ) : selectable.map((d) => (
                <label
                  key={d.id}
                  className="flex items-center gap-3 p-2.5 hover:bg-slate-50 cursor-pointer min-w-0"
                  data-testid={`bundle-doc-option-${d.id}`}
                >
                  <Checkbox
                    checked={!!checked[d.id]}
                    onCheckedChange={(v) => setChecked((prev) => ({ ...prev, [d.id]: !!v }))}
                  />
                  {d.is_thread_attachment
                    ? <Paperclip className="w-4 h-4 text-amber-500 shrink-0" />
                    : <FileText className="w-4 h-4 text-blue-600 shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate" title={d.filename}>{d.filename}</p>
                    <p className="text-[11px] text-slate-500 truncate">
                      {d.is_thread_attachment
                        ? `Attachment · ${d.parent_doc_type || ''} · v${d.related_to_version ?? '?'}`
                        : `${d.doc_type} · v${d.version}`}
                    </p>
                  </div>
                  {d.is_thread_attachment
                    ? <Badge variant="outline" className="text-[10px] flex-shrink-0 border-amber-300 text-amber-700 bg-amber-50">attachment</Badge>
                    : (Array.isArray(d.source_document_ids) && d.source_document_ids.length > 0
                        ? <Badge className="text-[10px] flex-shrink-0 gap-1 bg-indigo-100 text-indigo-700 border border-indigo-300"><Layers className="w-3 h-3" />Bundle · v{d.version}</Badge>
                        : <Badge variant="outline" className="text-[10px] flex-shrink-0">v{d.version}</Badge>)}
                </label>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2 cursor-pointer" data-testid="bundle-include-cover">
            <Checkbox checked={includeCover} onCheckedChange={(v) => setIncludeCover(!!v)} />
            <span className="text-sm text-slate-700">Include request form as cover pages</span>
          </label>
        </div>

        <DialogFooter className="px-6 py-3 border-t border-slate-200 bg-slate-50">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={generating}>Cancel</Button>
          <Button
            onClick={handleGenerate}
            disabled={generating || selectedIds.length === 0 || !name.trim() || !finalDocType}
            className="bg-blue-900 hover:bg-blue-950"
            data-testid="generate-bundle-confirm-btn"
          >
            {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Layers className="w-4 h-4 mr-2" />}
            {generating ? 'Generating…' : `Generate Bundle (${selectedIds.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default GenerateBundleDialog;
